"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { Plug, Search, Sparkles, ArrowLeft, Check, Clock, Zap, Filter } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { ChannelLogo } from "@/components/landing/ChannelLogo";
import {
  ShimmerBar,
  EmptyState,
  ErrorState,
} from "@/components/site/StatePrimitives";
import { seoMeta, seoLinks, jsonLd, breadcrumbLd, itemListLd, organizationLd } from "@/lib/seo/seo";

type Cat = "all" | "messaging" | "social" | "ecommerce" | "analytics" | "payments" | "tools";
type Status = "live" | "beta" | "soon";

type Integration = {
  id: string;
  name: string;
  logo?: string;
  fallback?: string;
  cat: Exclude<Cat, "all">;
  catLabel: string;
  desc: string;
  status: Status;
  popular?: boolean;
  color: string;
};

const CHANNEL_NAMES = ["Instagram", "Telegram", "WhatsApp"];

const ITEMS: Integration[] = [
  {
    id: "ig",
    name: "Instagram",
    logo: "Instagram",
    cat: "social",
    catLabel: "شبکه اجتماعی",
    desc: "DM، کامنت، استوری و ریل خودکار.",
    status: "live",
    popular: true,
    color: "from-pink to-secondary-main",
  },
  {
    id: "wa",
    name: "WhatsApp Business",
    logo: "WhatsApp",
    cat: "messaging",
    catLabel: "پیام‌رسان",
    desc: "پاسخ خودکار، کاتالوگ و ثبت سفارش.",
    status: "live",
    popular: true,
    color: "from-teal to-primary-main",
  },
  {
    id: "tg",
    name: "Telegram",
    logo: "Telegram",
    cat: "messaging",
    catLabel: "پیام‌رسان",
    desc: "ربات، گروه و کانال هوشمند.",
    status: "live",
    popular: true,
    color: "from-light-blue to-blue",
  },
  {
    id: "sms",
    name: "پنل پیامک",
    fallback: "SMS",
    cat: "messaging",
    catLabel: "پیام‌رسان",
    desc: "اتصال به چندین پنل ایرانی.",
    status: "live",
    color: "from-primary-main to-primary-300",
  },
  {
    id: "web",
    name: "Web Chat",
    fallback: "WEB",
    cat: "messaging",
    catLabel: "پیام‌رسان",
    desc: "ویجت چت سایت با پرسونای اختصاصی.",
    status: "live",
    color: "from-purple to-blue",
  },
  {
    id: "shopify",
    name: "Shopify",
    fallback: "SH",
    cat: "ecommerce",
    catLabel: "فروشگاه",
    desc: "همگام‌سازی محصول، سفارش و مشتری.",
    status: "beta",
    color: "from-secondary-main to-secondary-300",
  },
  {
    id: "woo",
    name: "WooCommerce",
    fallback: "Woo",
    cat: "ecommerce",
    catLabel: "فروشگاه",
    desc: "اتصال مستقیم به فروشگاه وردپرسی.",
    status: "live",
    color: "from-purple to-pink",
  },
  {
    id: "digikala",
    name: "دیجی‌کالا",
    fallback: "DK",
    cat: "ecommerce",
    catLabel: "فروشگاه",
    desc: "همگام‌سازی موجودی و سفارشات.",
    status: "soon",
    color: "from-error to-secondary-main",
  },
  {
    id: "ga",
    name: "Google Analytics",
    fallback: "GA",
    cat: "analytics",
    catLabel: "آنالیتیکس",
    desc: "ترکینگ کانال‌ها و کمپین‌ها.",
    status: "live",
    color: "from-secondary-main to-error",
  },
  {
    id: "meta",
    name: "Meta Pixel",
    fallback: "fb",
    cat: "analytics",
    catLabel: "آنالیتیکس",
    desc: "ردیابی conversion چندکاناله.",
    status: "live",
    color: "from-blue to-purple",
  },
  {
    id: "mixpanel",
    name: "Mixpanel",
    fallback: "MX",
    cat: "analytics",
    catLabel: "آنالیتیکس",
    desc: "آنالیز event-based پیشرفته.",
    status: "beta",
    color: "from-purple to-light-blue",
  },
  {
    id: "zarinpal",
    name: "زرین‌پال",
    fallback: "Z",
    cat: "payments",
    catLabel: "پرداخت",
    desc: "درگاه پرداخت داخل گفت‌وگو.",
    status: "live",
    popular: true,
    color: "from-primary-main to-teal",
  },
  {
    id: "idpay",
    name: "IDPay",
    fallback: "ID",
    cat: "payments",
    catLabel: "پرداخت",
    desc: "لینک پرداخت سریع و فاکتور.",
    status: "live",
    color: "from-primary-300 to-light-blue",
  },
  {
    id: "stripe",
    name: "Stripe",
    fallback: "S",
    cat: "payments",
    catLabel: "پرداخت",
    desc: "پرداخت بین‌المللی برای صادرات.",
    status: "soon",
    color: "from-purple to-blue",
  },
  {
    id: "zapier",
    name: "Zapier",
    fallback: "ZP",
    cat: "tools",
    catLabel: "اتوماسیون",
    desc: "اتصال به ۵٬۰۰۰+ سرویس دیگر.",
    status: "beta",
    color: "from-secondary-main to-pink",
  },
  {
    id: "sheets",
    name: "Google Sheets",
    fallback: "GS",
    cat: "tools",
    catLabel: "ابزار",
    desc: "خروجی خودکار داده‌ها.",
    status: "live",
    color: "from-success to-teal",
  },
  {
    id: "slack",
    name: "Slack",
    fallback: "SL",
    cat: "tools",
    catLabel: "ابزار",
    desc: "اعلان به تیم در زمان وقوع رویداد.",
    status: "live",
    color: "from-purple to-pink",
  },
  {
    id: "tiktok",
    name: "TikTok",
    fallback: "TT",
    cat: "social",
    catLabel: "شبکه اجتماعی",
    desc: "مدیریت کامنت و پیام.",
    status: "soon",
    color: "from-pink to-blue",
  },
];

const CATS: { id: Cat; label: string }[] = [
  { id: "all", label: "همه" },
  { id: "messaging", label: "پیام‌رسان" },
  { id: "social", label: "شبکه اجتماعی" },
  { id: "ecommerce", label: "فروشگاه" },
  { id: "payments", label: "پرداخت" },
  { id: "analytics", label: "آنالیتیکس" },
  { id: "tools", label: "ابزار" },
];

type Scenario =
  "loaded" | "loading" | "empty" | "error" | "popular-only" | "soon-only" | "no-results";

export default function IntegrationsPage() {
  const [scenario, setScenario] = useState<Scenario>("loading");
  const [cat, setCat] = useState<Cat>("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    if (scenario !== "loading") return;
    const t = setTimeout(() => setScenario("loaded"), 1000);
    return () => clearTimeout(t);
  }, [scenario]);

  const items = useMemo(() => {
    if (scenario === "empty" || scenario === "no-results") return [];
    let l = ITEMS;
    if (scenario === "popular-only") l = l.filter((i) => i.popular);
    if (scenario === "soon-only") l = l.filter((i) => i.status === "soon");
    if (cat !== "all") l = l.filter((i) => i.cat === cat);
    if (q.trim()) {
      const t = q.trim().toLowerCase();
      l = l.filter((i) => i.name.toLowerCase().includes(t) || i.desc.toLowerCase().includes(t));
    }
    return l;
  }, [scenario, cat, q]);

  return (
    <SiteShell>
      <Hero />

      <section className="mx-auto max-w-7xl px-5 sm:px-6">
        <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-border bg-card/60 p-3 backdrop-blur-sm sm:flex-row sm:items-center">
          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-border bg-background px-3 py-2">
            <Search size={15} className="text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="جست‌وجوی یکپارچگی..."
              className="w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
            <Filter size={14} className="shrink-0 text-muted-foreground" />
            {CATS.map((c) => (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                className={`shrink-0 rounded-full border px-3 py-1.5 text-[11.5px] font-black ${
                  cat === c.id
                    ? "border-primary-main bg-primary-main/15 text-primary-main"
                    : "border-border text-muted-foreground hover:border-primary-main/40"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-12 sm:px-6">
        {scenario === "loading" ? (
          <Skel />
        ) : scenario === "error" ? (
          <ErrorState onRetry={() => setScenario("loading")} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Plug}
            title={scenario === "no-results" ? "نتیجه‌ای پیدا نشد" : "هنوز یکپارچگی فعالی نیست"}
            desc={
              scenario === "no-results"
                ? "فیلتر یا کلیدواژه را تغییر بده."
                : "به‌زودی ده‌ها یکپارچگی اضافه می‌شود."
            }
            cta={
              <button
                onClick={() => {
                  setCat("all");
                  setQ("");
                  setScenario("loaded");
                }}
                className="rounded-xl bg-primary-main px-5 py-2.5 text-[12.5px] font-black text-white hover:bg-primary-400"
              >
                پاکسازی فیلترها
              </button>
            }
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((it, i) => (
              <Card key={it.id} it={it} index={i} />
            ))}
          </div>
        )}
      </section>

      <Showcase />
      <CTA />
    </SiteShell>
  );
}

function Hero() {
  return (
    <section className="mx-auto max-w-7xl px-5 pt-16 pb-10 sm:px-6 sm:pt-24">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
          <Plug size={14} className="text-primary-main" />
          <span className="text-[12px] font-bold text-muted-foreground">
            ۲۰+ یکپارچگی فعال و در راه
          </span>
        </div>
        <h1 className="mx-auto mb-5 max-w-3xl text-4xl font-black leading-[1.15] tracking-tight sm:text-5xl lg:text-6xl">
          به هر چیزی که داری
          <span className="block brand-gradient-text">وصل می‌شود</span>
        </h1>
        <p className="mx-auto max-w-2xl text-[14px] leading-relaxed text-muted-foreground sm:text-[15px]">
          از پیام‌رسان‌ها تا فروشگاه‌ها و درگاه‌های پرداخت — همه در یک پلتفرم همگام و هوشمند.
        </p>

        {/* logo strip */}
        <div className="mx-auto mt-8 flex max-w-3xl flex-wrap items-center justify-center gap-3">
          {CHANNEL_NAMES.map((n) => (
            <div
              key={n}
              className="flex items-center gap-2 rounded-2xl border border-border bg-card px-3.5 py-2"
            >
              <ChannelLogo name={n} className="h-5 w-5" />
              <span className="text-[12px] font-black text-foreground">{n}</span>
            </div>
          ))}
          <div className="flex items-center gap-2 rounded-2xl border border-dashed border-border bg-background px-3.5 py-2 text-[11.5px] font-black text-muted-foreground">
            +۱۵
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function Card({ it, index }: { it: Integration; index: number }) {
  const tone =
    it.status === "live"
      ? { bg: "bg-success/15", text: "text-success", icon: <Check size={10} />, label: "فعال" }
      : it.status === "beta"
        ? {
            bg: "bg-secondary-main/15",
            text: "text-secondary-main",
            icon: <Sparkles size={10} />,
            label: "بتا",
          }
        : {
            bg: "bg-muted",
            text: "text-muted-foreground",
            icon: <Clock size={10} />,
            label: "به‌زودی",
          };
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: (index % 12) * 0.03 }}
      className="group relative flex flex-col rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-primary-main/40 hover:shadow-xl hover:shadow-primary-main/10"
    >
      {it.popular && (
        <div className="absolute left-3 top-3 rounded-full bg-secondary-main/15 px-2 py-0.5 text-[9px] font-black text-secondary-main">
          ★ محبوب
        </div>
      )}
      <div
        className={`mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${it.color} text-[12px] font-black text-white shadow-md`}
      >
        {it.logo ? <ChannelLogo name={it.logo} className="h-6 w-6" /> : it.fallback}
      </div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className="text-[14px] font-black text-foreground">{it.name}</h3>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9.5px] font-black ${tone.bg} ${tone.text}`}
        >
          {tone.icon} {tone.label}
        </span>
      </div>
      <div className="mb-3 text-[10.5px] text-muted-foreground">{it.catLabel}</div>
      <p className="flex-1 text-[12px] leading-relaxed text-muted-foreground">{it.desc}</p>
      <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
        <button
          disabled={it.status === "soon"}
          className={`text-[11.5px] font-black ${
            it.status === "soon"
              ? "text-muted-foreground"
              : "text-primary-main hover:text-primary-400"
          }`}
        >
          {it.status === "soon" ? "اطلاع به من" : "اتصال →"}
        </button>
        <Zap
          size={12}
          className={it.status === "live" ? "text-success" : "text-muted-foreground/60"}
        />
      </div>
    </motion.div>
  );
}

function Skel() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="space-y-3 rounded-2xl border border-border bg-card p-5">
          <ShimmerBar className="h-12 w-12 rounded-2xl" />
          <ShimmerBar className="h-4 w-2/3" />
          <ShimmerBar className="h-3 w-1/3" />
          <ShimmerBar className="h-3 w-full" />
          <ShimmerBar className="h-3 w-5/6" />
          <ShimmerBar className="h-8 w-24" />
        </div>
      ))}
    </div>
  );
}

function Showcase() {
  return (
    <section className="mx-auto max-w-7xl px-5 pb-16 sm:px-6">
      <div className="grid gap-5 lg:grid-cols-3">
        {[
          {
            title: "اتصال در ۲ دقیقه",
            desc: "هیچ کد و توسعه‌ای لازم نیست. OAuth و wizard ساده.",
            icon: Zap,
            color: "from-primary-main to-primary-300",
          },
          {
            title: "همگام‌سازی Real-time",
            desc: "وبهوک‌های دوطرفه با تأخیر کمتر از ۱ ثانیه.",
            icon: Sparkles,
            color: "from-secondary-main to-secondary-300",
          },
          {
            title: "API باز و وبهوک سفارشی",
            desc: "هر چیزی که اینجا نیست را با API خودت بساز.",
            icon: Plug,
            color: "from-purple to-blue",
          },
        ].map((b) => {
          const I = b.icon;
          return (
            <div key={b.title} className="rounded-2xl border border-border bg-card p-6">
              <div
                className={`mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${b.color} text-white shadow-md`}
              >
                <I size={18} />
              </div>
              <h3 className="mb-2 text-[15px] font-black text-foreground">{b.title}</h3>
              <p className="text-[12.5px] leading-relaxed text-muted-foreground">{b.desc}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="mx-auto max-w-5xl px-5 pb-24 sm:px-6">
      <div className="rounded-3xl border border-primary-main/30 bg-gradient-to-br from-primary-main/10 via-card to-secondary-main/10 p-8 text-center sm:p-12">
        <h2 className="mb-3 text-2xl font-black text-foreground sm:text-3xl">
          یکپارچگی موردنظرت اینجا نیست؟
        </h2>
        <p className="mx-auto mb-6 max-w-md text-[13px] text-muted-foreground">
          درخواست بده، تیم ما به‌شدت روی یکپارچگی‌های جدید کار می‌کند.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 rounded-xl bg-primary-main px-6 py-3 text-[13px] font-black text-white hover:bg-primary-400"
          >
            درخواست یکپارچگی <ArrowLeft size={14} />
          </Link>
          <Link
            href="/login"
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-6 py-3 text-[13px] font-black text-foreground hover:bg-muted"
          >
            شروع رایگان
          </Link>
        </div>
      </div>
    </section>
  );
}
